/**
 * Export/import du coffre au format Klavox, **ciphertext-only**
 * (`docs/ARCHITECTURE.md` §4) : un fichier exporte ne contient jamais un
 * secret en clair, seulement ce que verrait deja le serveur souverain
 * (`EncryptedVaultItem`/`EncryptedVaultFolder` + l'enveloppe de compte, sans
 * mot de passe maitre ni vaultKey). Rejouable uniquement par qui connait le
 * mot de passe maitre correspondant.
 */
import {
  mergeVaultRecords,
  type EncryptedVaultAttachment,
  type EncryptedVaultFolder,
  type EncryptedVaultItem,
} from '@klavox/vault-model';
import type { AccountEnvelope } from './types.js';

export const VAULT_EXPORT_FORMAT = 'klavox-vault-export/v1';

export interface VaultExportFile {
  readonly format: typeof VAULT_EXPORT_FORMAT;
  readonly exportedAt: number;
  readonly account: AccountEnvelope;
  readonly items: readonly EncryptedVaultItem[];
  readonly folders: readonly EncryptedVaultFolder[];
  /** Pieces jointes chiffrees ; absent des exports anterieurs (lus comme `[]`). */
  readonly attachments?: readonly EncryptedVaultAttachment[];
}

export interface BuildVaultExportInput {
  readonly account: AccountEnvelope;
  readonly items: readonly EncryptedVaultItem[];
  readonly folders: readonly EncryptedVaultFolder[];
  readonly attachments?: readonly EncryptedVaultAttachment[];
  readonly now?: number;
}

export function buildVaultExport(input: BuildVaultExportInput): VaultExportFile {
  return {
    format: VAULT_EXPORT_FORMAT,
    exportedAt: input.now ?? Date.now(),
    account: input.account,
    items: input.items,
    folders: input.folders,
    attachments: input.attachments ?? [],
  };
}

export function serializeVaultExport(file: VaultExportFile): string {
  return JSON.stringify(file, null, 2);
}

class VaultExportParseError extends Error {}

function isEncryptedRecordShape(value: unknown): value is { readonly id: string; readonly revision: number } {
  if (value === null || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.id === 'string' && typeof candidate.revision === 'number';
}

/**
 * Parse et valide (forme minimale) un fichier d'export.
 *
 * @throws {VaultExportParseError} JSON invalide, format inconnu, ou tableaux
 *   `items`/`folders` malformes
 */
export function parseVaultExport(text: string): VaultExportFile {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new VaultExportParseError('JSON invalide');
  }
  if (raw === null || typeof raw !== 'object') {
    throw new VaultExportParseError('fichier d\'export: objet attendu');
  }
  const candidate = raw as Record<string, unknown>;
  if (candidate.format !== VAULT_EXPORT_FORMAT) {
    throw new VaultExportParseError(`format d'export inconnu: ${String(candidate.format)}`);
  }
  if (!Array.isArray(candidate.items) || !candidate.items.every(isEncryptedRecordShape)) {
    throw new VaultExportParseError('items: tableau d\'entrees chiffrees attendu');
  }
  if (!Array.isArray(candidate.folders) || !candidate.folders.every(isEncryptedRecordShape)) {
    throw new VaultExportParseError('folders: tableau de dossiers chiffres attendu');
  }
  if (candidate.attachments !== undefined && (!Array.isArray(candidate.attachments) || !candidate.attachments.every(isEncryptedRecordShape))) {
    throw new VaultExportParseError('attachments: tableau de pieces jointes chiffrees attendu');
  }
  if (candidate.account === null || typeof candidate.account !== 'object') {
    throw new VaultExportParseError('account: enveloppe de compte attendue');
  }
  return raw as VaultExportFile;
}

export interface MergeImportResult {
  readonly items: readonly EncryptedVaultItem[];
  readonly folders: readonly EncryptedVaultFolder[];
  readonly attachments: readonly EncryptedVaultAttachment[];
  readonly itemConflicts: number;
  readonly folderConflicts: number;
}

/**
 * Fusionne un export importe dans l'etat local (meme resolution de conflits
 * que la synchro serveur, revision/horodatage la plus recente gagne).
 */
export function mergeVaultExport(
  local: {
    readonly items: readonly EncryptedVaultItem[];
    readonly folders: readonly EncryptedVaultFolder[];
    readonly attachments?: readonly EncryptedVaultAttachment[];
  },
  imported: VaultExportFile,
): MergeImportResult {
  const items = mergeVaultRecords(local.items, imported.items);
  const folders = mergeVaultRecords(local.folders, imported.folders);
  const attachments = mergeVaultRecords(local.attachments ?? [], imported.attachments ?? []);
  return {
    items: items.merged,
    folders: folders.merged,
    attachments: attachments.merged,
    itemConflicts: items.conflicts.length,
    folderConflicts: folders.conflicts.length + attachments.conflicts.length,
  };
}

export { VaultExportParseError };
